import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Check, PlaylistAddCheck } from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { ActionsButton, SettingsCard } from '../../components/composed';
import { EventCreationForm } from '../../components/domain/event';
import { ApproveOrDeny } from '../../components/domain/object_management';
import CallToAction from '../DataBuilderPage/CallToAction';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (err) {
    return dateTimeString;
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.display({
    id: 'select',
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Select all events' }}
        size="small"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': `Select event ${row.original.id}` }}
        size="small"
      />
    ),
  }),
  columnHelper.accessor((row) => row.name ?? '', {
    id: 'name',
    header: 'Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.start_time ?? '', {
    id: 'start_time',
    header: 'Start Time',
    sortingFn: 'datetime',
    cell: (info) => formatDateTime(info.getValue()),
  }),
  columnHelper.accessor((row) => row.end_time ?? '', {
    id: 'end_time',
    header: 'End Time',
    sortingFn: 'datetime',
    cell: (info) => formatDateTime(info.getValue()),
  }),
  columnHelper.accessor((row) => row.cost ?? '', {
    id: 'cost',
    header: 'Cost',
    sortingFn: 'basic',
    cell: (info) => formatCurrency(info.row.original.cost),
  }),
  columnHelper.accessor('is_approved', {
    id: 'is_approved',
    header: 'Verified',
    sortingFn: 'basic',
    cell: (info) => (info.getValue() ? <Check sx={{ color: '#4caf50' }} /> : null),
  }),
];

const Events = ({ resourceId, facilityId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [sorting, setSorting] = useState([{ id: 'start_time', desc: false }]);
  const [rowSelection, setRowSelection] = useState({});
  const [manageOpen, setManageOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const table = useReactTable({
    data: events,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedEventIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.id);

  useEffect(() => {
    if (resourceId || facilityId) {
      fetchEvents();
    }
  }, [resourceId, facilityId]);

  // Refresh list when navigating back to this page (location key changes)
  useEffect(() => {
    if (resourceId || facilityId) {
      fetchEvents();
    }
  }, [location.key]);

  // Refresh list when window regains focus (user navigates back)
  useEffect(() => {
    const handleFocus = () => {
      if (resourceId || facilityId) {
        fetchEvents();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [resourceId, facilityId]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameter based on which prop is provided
      let queryParam = '';
      if (resourceId) {
        queryParam = `?resource=${resourceId}`;
      } else if (facilityId) {
        queryParam = `?facility=${facilityId}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/events/${queryParam}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleAddEvent = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveSuccess = (savedEvent) => {
    // Close modal
    setOpen(false);
    // Refresh events list to show the new event
    fetchEvents();
  };

  const handleManageSelected = () => {
    setBulkError(null);
    setManageOpen(true);
  };

  const handleManageClose = () => {
    if (!bulkSubmitting) {
      setManageOpen(false);
      setBulkError(null);
    }
  };

  const handleBulkModeration = async (updates) => {
    try {
      setBulkSubmitting(true);
      setBulkError(null);

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/events/bulk/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ ids: selectedEventIds, ...updates }),
      });

      if (!response.ok) {
        let message = 'Failed to update the selected events. Please try again.';
        if (response.status === 403) {
          message = 'You do not have permission to manage events.';
        } else {
          try {
            const data = await response.json();
            message = data.error || data.detail || message;
          } catch (parseError) {
            // Fall back to the generic message when the body is not JSON
          }
        }
        setBulkError(message);
        return;
      }

      setManageOpen(false);
      setRowSelection({});
      fetchEvents();
    } catch (err) {
      console.error('Error updating selected events:', err);
      setBulkError('Network error. Please check your connection and try again.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const actionsButton = (
    <ActionsButton
      actions={[
        {
          id: 'add-event',
          label: 'Add Event',
          icon: <Add fontSize="small" />,
          hidden: !resourceId,
          onClick: handleAddEvent,
        },
        {
          id: 'manage-selected-events',
          label: 'Manage Selected Events',
          icon: <PlaylistAddCheck fontSize="small" />,
          disabled: selectedEventIds.length === 0,
          onClick: handleManageSelected,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <>
        <SettingsCard title="Events" endActions={actionsButton}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </SettingsCard>
        <CreateEventDialog
          open={open}
          onClose={handleClose}
          onSave={handleSaveSuccess}
          resourceId={resourceId}
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        <SettingsCard title="Events" endActions={actionsButton}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        </SettingsCard>
        {resourceId && (
          <CreateEventDialog
            open={open}
            onClose={handleClose}
            onSave={handleSaveSuccess}
            resourceId={resourceId}
          />
        )}
      </>
    );
  }

  return (
    <>
      <SettingsCard title="Events" endActions={actionsButton}>
      {events.length === 0 ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No events found.
            </Typography>
          </Box>
          <CallToAction />
        </>
      ) : (
        <>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sortDirection = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          onClick={
                            canSort
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                          style={{
                            textAlign: 'left',
                            paddingRight: 10,
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            ...(canSort ? { cursor: 'pointer' } : {}),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sortDirection === 'asc'
                            ? ' \u25B2'
                            : sortDirection === 'desc'
                            ? ' \u25BC'
                            : ''}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => handleRowClick(row.original.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          textAlign: 'left',
                          paddingRight: 10,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <CallToAction />
        </>
      )}
    </SettingsCard>

    {resourceId && (
      <CreateEventDialog
        open={open}
        onClose={handleClose}
        onSave={handleSaveSuccess}
        resourceId={resourceId}
      />
    )}

    <Dialog
      open={manageOpen}
      onClose={handleManageClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ p: 0 }}>
        <ApproveOrDeny
          title="Manage selected events"
          message={`${selectedEventIds.length} ${
            selectedEventIds.length === 1 ? 'event' : 'events'
          } selected. Approving marks them as verified. Denying removes them from the list.`}
          errorMessage={bulkError}
          disabled={bulkSubmitting}
          onApprove={() => handleBulkModeration({ is_approved: true })}
          onDeny={() => handleBulkModeration({ is_active: false })}
          onCancel={handleManageClose}
        />
      </DialogContent>
    </Dialog>
  </>
  );
};

// CreateEventDialog Component - uses reusable EventCreationForm
const CreateEventDialog = ({ open, onClose, onSave, resourceId }) => {
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Event</DialogTitle>
      <DialogContent>
        <EventCreationForm
          resourceId={resourceId}
          onSaved={onSave}
          onCancel={handleClose}
          showActions={true}
          submitLabel="Save"
        />
      </DialogContent>
    </Dialog>
  );
};

export default Events;
